// global
import React, { useEffect, useState, isValidElement, useRef } from 'react';
import PropTypes from 'prop-types';
// components
import { Grid } from 'semantic-ui-react';
// icons
import AboutIcon from 'assets/icon/about-icon/AboutIcon';
import ProblemIcon from 'assets/icon/problem-icon/ProblemIcon';
import CloseIcon from 'assets/icon/close-icon/CloseIcon';
// redux
import { useDispatch } from 'react-redux';
import { dismissAlertBand } from 'app/redux/actions/alertBand.action';
// style
import style from 'app/components/commons/alertBand/AlertBand.module.scss';

export default function AlertBand(props) {
  const { id, content, variant, icon, actionContent, autoClose, timeout, expendable } = props;

  const [slideIn, setSlideIn] = useState(true);
  const [breakLineAction, setBreakLineAction] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const dispatch = useDispatch();

  const alertRef = useRef(null);
  const currentTimeRef = useRef(0);

  useEffect(() => {
    let timeoutToSlideOut;
    let timeoutToDismiss;

    if (autoClose) {
      const slideOut = () => {
        if (currentTimeRef.current >= timeout && !isHovering) {
          setSlideIn(false);
          timeoutToDismiss = setTimeout(() => {
            dispatch(dismissAlertBand(id));
          }, 700);
        } else {
          currentTimeRef.current += 500;
          timeoutToSlideOut = setTimeout(slideOut, 500);
        }
      };

      timeoutToSlideOut = setTimeout(slideOut, 1000);
    }

    return () => {
      clearInterval(timeoutToDismiss);
      clearInterval(timeoutToSlideOut);
    };
  }, [id, timeout, dispatch, autoClose, isHovering]);

  useEffect(() => {
    if (alertRef.current && alertRef.current.clientHeight > 30) {
      setBreakLineAction(true);
    }
  }, [alertRef]);

  const handlaClassName = () => {
    let className = style[variant];

    className += slideIn ? ` ${style['slideIn']}` : ` ${style['slideOut']}`;

    return className;
  };

  const handleIcon = () => {
    if (!icon) {
      return null;
    }

    let iconToRender;

    switch (variant) {
      case 'info': {
        iconToRender = <AboutIcon fill={'#fff'} size='14' />;
        break;
      }
      case 'negative': {
        iconToRender = <ProblemIcon fill={'#fff'} size='14' />;
        break;
      }
      default: {
        return false;
      }
    }

    return iconToRender ? <div className={style.icon}>{iconToRender}</div> : null;
  };

  const handleAction = () => {
    if (!actionContent) return null;

    return (
      <button
        onClick={e => {
          e.stopPropagation();
          return actionContent.action(e);
        }}
      >
        {actionContent.name}
      </button>
    );
  };

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  return (
    <Grid.Row
      id={style.alertBand}
      className={handlaClassName()}
      stretched
      verticalAlign={'middle'}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Grid.Column
        width={expendable ? 15 : 16}
        className={`${style.wrapperContent} ${expendable ? style.expendable : ''}`}
      >
        <div className={style.content} ref={alertRef}>
          <div className={style.mainContent}>
            {handleIcon()}
            {isValidElement(content) ? content : <p>{content}</p>}
          </div>

          {!!handleAction() && (
            <div
              className={`${style.actionButton} ${breakLineAction ? style.breakLineAction : ''}`}
            >
              {handleAction()}
            </div>
          )}
        </div>
      </Grid.Column>
      {expendable && (
        <Grid.Column width={1} className={style.wrapperClose}>
          <button onClick={() => dispatch(dismissAlertBand(id))}>
            <CloseIcon fill='#ffffff' size='8' />
          </button>
        </Grid.Column>
      )}
    </Grid.Row>
  );
}

AlertBand.defaultProps = {
  variant: 'neutral',
  autoClose: true,
  timeout: 7000,
  icon: true,
  expendable: true
};

AlertBand.propTypes = {
  id: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  variant: PropTypes.oneOf(['neutral', 'info', 'negative']),
  icon: PropTypes.bool,
  actionContent: PropTypes.shape({
    name: PropTypes.string,
    action: PropTypes.func
  }),
  autoClose: PropTypes.bool,
  timeout: PropTypes.number,
  expendable: PropTypes.bool
};
